import { Checkbox, Group, MultiSelect, Select, Stack } from '@mantine/core';
import type { EventAlert } from '../../../../../../api/events-client/events-client.type';
import {
  EVENT_MODEL_OPTIONS,
  MAPPING_RELATIONSHIP_OPTIONS,
  TASK_EVENT_TYPE_OPTIONS,
  type Step,
  type Workflow,
} from '../../../../../../api/workflows-client/workflows-client.type';
import i18n from '../../../../../../base-dictionary';
import { baseWhite, redBase, secondaryBase } from '../../../../../../styles/design-tokens';
import { Button } from '../../../../../../ui-library/button/button';
import { Divider } from '../../../../../../ui-library/divider/divider';
import { Icon } from '../../../../../../ui-library/icon/icon';
import { Tooltip } from '../../../../../../ui-library/tooltip/tooltip';
import { Text } from '../../../../../../ui-library/typography/typography';
import { DashboardModalStyled } from '../../../../../dashboards/components/dashboard-modal';
import { useWorkflowStepSettings } from './view-workflow-step-settings-modal.hooks';

type Props = {
  step: Step | null;
  workflow: Workflow | null;
  opened: boolean;
  onClose: () => void;
  onConfirm: (updatedStep: Step) => void;
};

export const ViewWorkflowStepSettingsModal = ({ step, workflow, opened, onClose, onConfirm }: Props) => {
  const {
    form,
    isAddingAlert,
    setIsAddingAlert,
    isEditingAlert,
    isUpdating,
    newAlertValues,
    setNewAlertValues,
    isCreating,
    isDeleting,
    isUpdatingAlert,
    eventAlertsQuery,
    handleOnClose,
    handleSubmit,
    handleAddAlert,
    handleEditAlert,
    handleUpdateAlert,
    handleCancelEdit,
    handleRemoveAlert,
  } = useWorkflowStepSettings(step, workflow, onClose, onConfirm);

  // Show loading state while fetching event alerts
  if (eventAlertsQuery.isLoading && step?.id) {
    return (
      <DashboardModalStyled
        title={
          <Text size="large" weight="medium">
            {`Edit ${step?.name} Settings`}
          </Text>
        }
        opened={opened}
        onClose={handleOnClose}
        centered
        size="xl"
      >
        <Text>Loading...</Text>
      </DashboardModalStyled>
    );
  }

  return (
    <DashboardModalStyled
      title={
        <Text size="large" weight="medium">
          {`Edit ${step?.name} Settings`}
        </Text>
      }
      opened={opened}
      onClose={handleOnClose}
      centered
      size="xl"
    >
      <form onSubmit={form.onSubmit(handleSubmit)}>
        <Stack gap="sm">
          <Group justify="space-between">
            <Text weight="medium">Statuses</Text>
            <Group gap="xs">
              <Tooltip
                label={`An important status means that every ${i18n.t('common.work_unit')} should enter this status. If a ${i18n.t('common.work_unit')} skips this status, alerts will be triggered.`}
                variant="black"
                multiline
                w={250}
                zIndex={1000}
              >
                <Icon name="info" color={secondaryBase} size={16} />
              </Tooltip>
              <Text weight="medium">Is Important</Text>
            </Group>
          </Group>
          {form.values.mappings.map((mapping, index) => (
            <Group key={mapping.id} justify="space-between">
              <Text>{mapping.external_name}</Text>
              <Checkbox
                checked={mapping.is_important}
                color={secondaryBase}
                onChange={(event) => {
                  form.setFieldValue(`mappings.${index}.is_important`, event.currentTarget.checked);
                }}
              />
            </Group>
          ))}
        </Stack>
        <Divider style={{ margin: '24px 0' }} />
        <Stack gap="md">
          <Group justify="space-between">
            <Group gap="xs">
              <Text weight="medium">{`${i18n.t('process.event_graph.event')} Alerts`}</Text>
              <Tooltip
                label={`Alerts are triggered when a ${i18n.t('common.work_unit')} has an event of the selected type occur either inside or outside of the selected statuses.`}
                variant="black"
                multiline
                w={250}
                zIndex={1000}
              >
                <Icon name="info" color={secondaryBase} size={16} />
              </Tooltip>
            </Group>
            <Button
              variant="secondary"
              size="xs"
              radius="xl"
              leftSection={
                <Icon name="add_circle_outline" size={16} color={isAddingAlert ? secondaryBase : baseWhite} />
              }
              onClick={() => setIsAddingAlert(true)}
              disabled={isAddingAlert || isCreating || isEditingAlert}
            >
              Add
            </Button>
          </Group>

          {form.values.event_alerts.map((alert: EventAlert, index: number) => (
            <Group key={alert.id} justify="space-between">
              <Group gap="sm">
                <Text size="small" weight="medium">
                  {`Alert #${index + 1}`}
                </Text>
                <Text size="small">
                  Model: {EVENT_MODEL_OPTIONS.find((option) => option.value === alert.event_model)?.label}
                </Text>
                <Text size="small">|</Text>
                <Text size="small">
                  Type: {TASK_EVENT_TYPE_OPTIONS.find((option) => option.value === alert.raw_event_type)?.label}
                </Text>
                <Text size="small">|</Text>
                <Text size="small">
                  {`${MAPPING_RELATIONSHIP_OPTIONS.find((option) => option.value === alert.step_mapping_relationship)?.label} statuses: ${alert.step_mapping_names.join(', ')}`}
                </Text>
              </Group>
              <Group gap="xs">
                <Button
                  variant="subtle"
                  size="xs"
                  radius="md"
                  color={secondaryBase}
                  onClick={() => handleEditAlert(alert)}
                  disabled={isDeleting || isUpdatingAlert || isAddingAlert || isEditingAlert}
                >
                  <Icon name="edit" size={16} />
                </Button>
                <Button
                  variant="subtle"
                  size="xs"
                  radius="md"
                  color={redBase}
                  onClick={() => handleRemoveAlert(alert.id)}
                  disabled={isDeleting || isUpdatingAlert || isAddingAlert || isEditingAlert}
                >
                  <Icon name="delete" size={16} />
                </Button>
              </Group>
            </Group>
          ))}

          {(isAddingAlert || isEditingAlert) && (
            <Stack gap="xs">
              <Group justify="space-between">
                <Text size="small" weight="medium">
                  {isEditingAlert ? 'Edit Alert' : 'New Alert'}
                </Text>
                <Group>
                  <Button
                    variant="outline"
                    size="xs"
                    radius="xl"
                    onClick={isEditingAlert ? handleCancelEdit : () => setIsAddingAlert(false)}
                  >
                    Cancel
                  </Button>
                  <Button
                    variant="primary"
                    size="xs"
                    radius="xl"
                    onClick={isEditingAlert ? handleUpdateAlert : handleAddAlert}
                    disabled={
                      !newAlertValues.raw_event_type ||
                      newAlertValues.step_mapping_names.length === 0 ||
                      isCreating ||
                      isUpdatingAlert
                    }
                    loading={isCreating || isUpdatingAlert}
                  >
                    {isEditingAlert ? 'Update Alert' : 'Add Alert'}
                  </Button>
                </Group>
              </Group>
              <Group justify="space-between">
                <Select
                  label="Event Model"
                  data={EVENT_MODEL_OPTIONS}
                  value={newAlertValues.event_model}
                  onChange={(value) =>
                    setNewAlertValues({ ...newAlertValues, event_model: value as 'task' | 'change_request' })
                  }
                  required
                  comboboxProps={{ withinPortal: true, position: 'top', zIndex: 1000 }}
                  disabled
                  radius="xl"
                  w={150}
                />
                <Select
                  label="Event Type"
                  data={newAlertValues.event_model === 'task' ? TASK_EVENT_TYPE_OPTIONS : []}
                  value={newAlertValues.raw_event_type}
                  onChange={(value) => setNewAlertValues({ ...newAlertValues, raw_event_type: value as string })}
                  placeholder="e.g., status, assignee"
                  required
                  comboboxProps={{ withinPortal: true, position: 'top', zIndex: 1000 }}
                  radius="xl"
                  w={175}
                />
                <Select
                  label="Mapping Relationship"
                  data={MAPPING_RELATIONSHIP_OPTIONS}
                  value={newAlertValues.step_mapping_relationship}
                  onChange={(value) =>
                    setNewAlertValues({
                      ...newAlertValues,
                      step_mapping_relationship: value as 'includes' | 'excludes',
                    })
                  }
                  required
                  comboboxProps={{ withinPortal: true, position: 'top', zIndex: 1000 }}
                  radius="xl"
                  w={175}
                />
                <MultiSelect
                  label="Status Mappings"
                  data={step?.mappings.map((m) => ({ value: m.external_name, label: m.external_name })) || []}
                  value={newAlertValues.step_mapping_names}
                  onChange={(value) =>
                    setNewAlertValues({
                      ...newAlertValues,
                      step_mapping_names: Array.isArray(value) ? value : [value as string],
                    })
                  }
                  placeholder="Select statuses"
                  required
                  comboboxProps={{ withinPortal: true, position: 'top', zIndex: 1000 }}
                  radius="xl"
                  w={175}
                />
              </Group>
            </Stack>
          )}
        </Stack>

        <Group justify="flex-end" style={{ marginTop: '24px' }}>
          <Button variant="outline" radius="xl" size="sm" onClick={() => handleOnClose()}>
            Cancel
          </Button>
          <Button
            type="submit"
            variant="primary"
            radius="xl"
            size="sm"
            leftSection={<Icon name="check_circle" color={baseWhite} size={16} />}
            loading={isUpdating}
            disabled={isUpdating}
          >
            Save Changes
          </Button>
        </Group>
      </form>
    </DashboardModalStyled>
  );
};
